import { createElement, forwardRef, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { TileContainerProps } from '.';
import { useSingleAndDoubleClick } from '../../../hooks/useSingleAndDoubleClick';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../../theme';
import { markEvent, ReactMouseEvent } from '../../../util/events';
import { setRef } from '../../../util/ref-util';

import cx from 'classnames';
import cn from './TileContainer.css.json';
import './TileContainer.css';

export const TileContainer = forwardRef<HTMLDivElement, TileContainerProps>(function TileContainer(
  props,
  ref
) {
  const dt = useDesignTokens();
  const tileRef = useRef<HTMLDivElement | null>(null);

  const onSingleClick = (e: ReactMouseEvent) => {
    props.onClick?.();
    const selection = window.getSelection()?.toString() ?? '';
    // Prevent tile selection if click resulted in text selection.
    if (e.isDefaultPrevented() || selection.length > 0) return;
    props.onSelect?.({
      selectionArea: 'tile',
      index: props.index,
      altKey: e.altKey,
      ctlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey,
    });
  };
  const onDoubleClick = (e: ReactMouseEvent) => {
    e.preventDefault();
    props.onOpen?.();
  };

  const clickHandlers = useSingleAndDoubleClick({
    persist: true,
    onSingleClick,
    onDoubleClick,
  });
  const defaultRole = props.disableInteractiveMode ? 'option' : 'group';
  const browseModeRole = props.role ?? defaultRole;

  return (
    <div
      style={{
        ...renderInlineCssVars(getTileTokens(dt, props), 'tile'),
        ...props.style,
      }}
      onContextMenu={props.onContextMenu}
      ref={(element) => {
        setRef(ref, element);
        setRef(tileRef, element);
      }}
      className={cx(cn.tile, props.className)}
      role={props.isInteractive ? 'dialog' : browseModeRole}
      aria-label={props.ariaLabel}
      aria-labelledby={props.ariaLabelledBy}
      aria-describedby={props.ariaDescribedBy}
      tabIndex={props.tabIndex ?? 0}
      data-testid={props.testId}
      onClick={props.disableClickDelay ? onSingleClick : clickHandlers.onClick}
      onDoubleClick={props.disableClickDelay ? onDoubleClick : clickHandlers.onDoubleClick}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onKeyDown={(e) => {
        props.onKeyDown?.(e);

        // Early return if the event is fired on the controls inside of the tile
        if (e.target !== e.currentTarget) return;

        if (e.key === 'Enter' && props.onOpen) {
          props.onOpen();
          markEvent(e); // To prevent the default button in Dialog from being activated
          e.preventDefault();
        } else if (e.key === ' ' && props.onSelect) {
          props.onSelect({
            index: props.index ?? undefined,
            selectionArea: 'tile',
            altKey: e.altKey,
            ctlKey: e.ctrlKey,
            metaKey: e.metaKey,
            shiftKey: e.shiftKey,
          });
          e.preventDefault();
        }
      }}
      onMouseDown={(e) => {
        // In order to support multiple selection in TileCarousel, disable the text selection using Shift key
        // and to prevent double click text selection when tile can be activated
        if (e.shiftKey || (e.detail > 1 && props.onOpen)) e.preventDefault();
      }}
    >
      {props.disableInteractiveMode ? (
        props.children
      ) : (
        <FocusLock disabled={!props.isInteractive} {...props.focusLockProps}>
          {props.children}
        </FocusLock>
      )}
    </div>
  );
});

const getTileTokens = (dt: DesignTokens, props: TileContainerProps) => {
  return {
    ...dt.backgroundsDepthNova.small,
    bd: props.selected ? dt.stateSecondaryNova.selected.bd : dt.backgroundsDepthNova.small.bd,
    'spacing-s': dt.spacing.core.s,
    focus:
      props.variant === 'primary-light' && dt.themeVariant === 'light'
        ? 'var(--focus-dark-bg)'
        : 'var(--focus-light-bg)',
  };
};
